import { useContext, useEffect, useState } from 'react';
import 'react-loading-skeleton/dist/skeleton.css';
import { NavLink } from 'react-router-dom';
import CartContext from '../../contexts/CartContext';
import { IMAGE_BASE_URL, PRODUCT_CATALOG_ID } from '../../utils/api-client';
import useFacebookPixel from '../../utils/useFacebookPixel';
import './ProductVariantModal.css';
import QuantityInput from './QuantityInput';
import notFoundImg from './image.png';

const ProductVariantModal = ({
  id,
  slug,
  varients,
  stock,
  onClose,
  onVariantSelect,
  subcat_slug,
  product,
}) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const facebookPixel = useFacebookPixel();
  const { addToCart } = useContext(CartContext);

  useEffect(() => {
    if (product.varients && product.varients.length > 0) {
      setSelectedVariant(0);
    }
  }, [product]);

  const handleVariantClick = (index) => {
    setSelectedVariant(index);
    setSelectedImage(0); // Reset to first image when variant changes
  };

  const handleAddToCart = (product, quantity, selectedVariant) => {
    // Track AddToCart Event
    if (facebookPixel) {
      facebookPixel.trackEvent('AddToCart', {
        content_ids: [product.product_id],
        content_type: 'product',
        content_name: product.product_name,
        currency: 'PKR',
        quantity: quantity,
        product_catalog_id: PRODUCT_CATALOG_ID,
      });
    }
    addToCart(product, quantity, selectedVariant);
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}></div>
      <div
        className="modal fade show"
        style={{ display: 'block' }}
        tabIndex="-1"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl">
          <div className="modal-content">
            {/* Enhanced Modal Header */}
            <div className="modal-header border-0">
              <h5 className="modal-title fw-bold text-dark">
                Select Product Variant
              </h5>
              <button
                type="button"
                className="btn-close btn-close-dark"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>

            {/* Enhanced Modal Body */}
            <div className="modal-body px-4 py-3">
              <div className="container-fluid px-0">
                <div className="row g-4">
                  {/* Enhanced Product Image Section */}
                  <div className="col-lg-5 col-md-6">
                    <div className="product-image-section">
                      {/* Main Product Image */}
                      <div className="main-image-container mb-3">
                        <div className="border rounded-4 overflow-hidden bg-light d-flex align-items-center justify-content-center position-relative">
                          <img
                            src={
                              product.varients &&
                              product.varients[selectedVariant] &&
                              product.varients[selectedVariant]
                                .varient_images &&
                              product.varients[selectedVariant].varient_images[
                                selectedImage
                              ]
                                ? `${IMAGE_BASE_URL}/${product.varients[selectedVariant].varient_images[selectedImage].image_path}`
                                : notFoundImg
                            }
                            onError={(e) => {
                              e.target.onError = null;
                              e.target.src = notFoundImg;
                            }}
                            alt={product.product_name}
                            className="main-product-image"
                          />
                          {/* Image Navigation Arrows */}
                          {product.varients &&
                            product.varients[selectedVariant] &&
                            product.varients[selectedVariant].varient_images &&
                            product.varients[selectedVariant].varient_images
                              .length > 1 && (
                              <>
                                <button
                                  className="btn btn-light btn-sm position-absolute start-0 top-50 translate-middle-y ms-2 rounded-pill shadow-sm"
                                  onClick={() =>
                                    setSelectedImage((prev) =>
                                      prev === 0
                                        ? product.varients[selectedVariant]
                                            .varient_images.length - 1
                                        : prev - 1
                                    )
                                  }
                                  style={{ zIndex: 10 }}
                                >
                                  <i className="fas fa-chevron-left"></i>
                                </button>
                                <button
                                  className="btn btn-light btn-sm position-absolute end-0 top-50 translate-middle-y me-2 rounded-pill shadow-sm"
                                  onClick={() =>
                                    setSelectedImage((prev) =>
                                      prev ===
                                      product.varients[selectedVariant]
                                        .varient_images.length -
                                        1
                                        ? 0
                                        : prev + 1
                                    )
                                  }
                                  style={{ zIndex: 10 }}
                                >
                                  <i className="fas fa-chevron-right"></i>
                                </button>
                              </>
                            )}
                        </div>
                      </div>

                      {/* Thumbnail Images */}
                      <div className="thumbnail-container">
                        <div className="d-flex flex-wrap gap-2 justify-content-center">
                          {product &&
                            product.varients &&
                            product.varients[selectedVariant] &&
                            product.varients[selectedVariant].varient_images &&
                            product.varients[
                              selectedVariant
                            ].varient_images.map((image, index) => (
                              <div
                                key={index}
                                className={`thumbnail-wrapper ${
                                  selectedImage === index ? 'active' : ''
                                }`}
                                onClick={() => setSelectedImage(index)}
                              >
                                <img
                                  src={`${IMAGE_BASE_URL}/${image.image_path}`}
                                  alt={image.rimg_alt_content}
                                  className="thumbnail-image"
                                  onError={(e) => {
                                    e.target.onError = null;
                                    e.target.src = notFoundImg;
                                  }}
                                />
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Enhanced Product Details Section */}
                  <div className="col-lg-7 col-md-6">
                    {product.varients && product.varients.length > 0 && (
                      <div className="product-details h-100 d-flex flex-column">
                        {/* Product Title and Rating */}
                        <div className="mb-3">
                          <h5 className="product-title text-dark mb-2 fw-bold">
                            {product.product_name}
                            {product.varients[selectedVariant] && (
                              <span
                                className="variant-name d-block h5 mt-1"
                                style={{ color: '#791a89' }}
                              >
                                {product.varients[selectedVariant].p_v_name}
                              </span>
                            )}
                          </h5>

                          <div className="rating-section d-flex align-items-center gap-2 mb-3">
                            <div className="star-rating">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <i
                                  key={star}
                                  className={`fas fa-star ${
                                    star <= (product.avg_rating || 0)
                                      ? 'text-warning'
                                      : 'text-muted'
                                  }`}
                                  style={{ fontSize: '0.9rem' }}
                                ></i>
                              ))}
                            </div>
                            <span className="rating-text small text-muted">
                              ({product.count_rating || 0} reviews)
                            </span>
                          </div>
                        </div>

                        {/* Price Section */}
                        <div className="price-section mb-4">
                          <div className="d-flex align-items-baseline gap-2 mb-1">
                            <span
                              className="current-price h4 fw-bold"
                              style={{ color: '#791a89' }}
                            >
                              Rs{' '}
                              {product.varients[selectedVariant] &&
                                product.varients[selectedVariant].final_price}
                            </span>
                            {product.varients[selectedVariant] &&
                              product.varients[selectedVariant].final_price <
                                product.varients[selectedVariant]
                                  .product_price && (
                                <span className="original-price h6 text-muted text-decoration-line-through">
                                  Rs{' '}
                                  {
                                    product.varients[selectedVariant]
                                      .product_price
                                  }
                                </span>
                              )}
                          </div>
                          <small className="text-muted">Per item</small>
                        </div>

                        {/* Product Information */}
                        <div className="product-info mb-4">
                          <div className="row g-2 mb-3">
                            <div className="col-4 col-sm-3">
                              <small className="text-muted fw-medium">
                                Variant:
                              </small>
                            </div>
                            <div className="col-8 col-sm-9">
                              <small
                                className="fw-bold"
                                style={{ color: '#791a89' }}
                              >
                                {product.varients[selectedVariant] &&
                                  product.varients[selectedVariant].p_v_name}
                              </small>
                            </div>
                          </div>

                          <div className="row g-2 mb-3">
                            <div className="col-4 col-sm-3">
                              <small className="text-muted fw-medium">
                                Availability:
                              </small>
                            </div>
                            <div className="col-8 col-sm-9">
                              <small className="text-success fw-medium">
                                <i className="fas fa-check-circle me-1"></i>
                                In Stock
                              </small>
                            </div>
                          </div>
                        </div>

                        {/* Variant Selection */}
                        <div className="variant-selection mb-4">
                          <h6 className="fw-bold mb-3">Choose Variant:</h6>
                          <div className="variants-grid">
                            {product.varients &&
                              product.varients.map((variant, index) => (
                                <div
                                  key={index}
                                  className={`variant-option ${
                                    selectedVariant === index ? 'selected' : ''
                                  }`}
                                  onClick={() => handleVariantClick(index)}
                                >
                                  <div className="variant-image">
                                    <img
                                      src={
                                        variant.varient_images &&
                                        variant.varient_images[0]
                                          ? `${IMAGE_BASE_URL}/${variant.varient_images[0].image_path}`
                                          : notFoundImg
                                      }
                                      alt={variant.rimg_alt_content}
                                      onError={(e) => {
                                        e.target.onError = null;
                                        e.target.src = notFoundImg;
                                      }}
                                    />
                                  </div>
                                  <small className="variant-label text-center mt-1 fw-medium">
                                    {variant.p_v_name}
                                  </small>
                                </div>
                              ))}
                          </div>
                        </div>

                        {/* Quantity Section */}
                        <div className="quantity-section mb-4">
                          <h6 className="fw-bold mb-2">Quantity:</h6>
                          <QuantityInput
                            quantity={quantity}
                            setQuantity={setQuantity}
                            stock={product.stock}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer border-0 pt-0 px-4 pb-0 pb-sm-2">
              <div className="d-flex gap-2 w-100">
                {/* Close Button */}
                <button
                  type="button"
                  className="btn  btn-outline-secondary flex-fill order-3 order-sm-1"
                  onClick={onClose}
                >
                  <i className="fas fa-times"></i>
                  <span className="d-none d-sm-inline ms-2">Close</span>
                </button>

                {/* View Details */}
                <NavLink
                  to={`/shop/${subcat_slug}/${slug}/`}
                  className="btn btn-outline-dark flex-fill order-2 text-decoration-none"
                >
                  <i className="fas fa-eye"></i>
                  <span className="d-none d-sm-inline ms-2">View Details</span>
                </NavLink>

                {/* Add to Cart */}
                <button
                  type="button"
                  className="btn btn-primary flex-fill order-1 order-sm-3"
                  onClick={() =>
                    handleAddToCart(
                      product,
                      quantity,
                      product.varients[selectedVariant]
                    )
                  }
                  disabled={product.in_stock < 1}
                  style={{ backgroundColor: '#791a89', borderColor: '#791a89' }}
                >
                  <i className="fas fa-shopping-cart"></i>
                  <span className="d-none d-sm-inline ms-2">Add to Cart</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductVariantModal;
